// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { FunctionComponent, ReactElement, useEffect } from "react";
import {
   BrowserRouter as Router, Route, Routes, Navigate
} from "react-router-dom";
import {
   useAuthContext, AuthenticatedComponent
} from "@asgardeo/auth-react";
import { ReactNotifications } from "react-notifications-component";
import 'react-notifications-component/dist/theme.css'
import { AsgErrorBoundary } from "./AsgErrorBoundary";
import { LandingPage } from "./../pages/Landing";
import { LoggedOutPage } from "./../pages/LogOut";
import { NotFoundPage } from "../pages/PageNotFound";
import { CaseView } from "./../pages/CaseView";
import { BaseLayout } from "./../layouts/BaseLayout";
import { Accounts } from "./../pages/account/Accounts";
import { Projects } from "./../pages/project/Projects";
import { UserScan } from "./../pages/user-scan/UserScan";
import { AccountDetails } from "./../pages/account/AccountDetails";
import { ProjectDetails } from "./../pages/project/ProjectDetails";
import AuthorizedApp from "./Authorize";
import { UnauthorizedPage } from "../pages/Unauthorized";
import { ItemNotFoundPage } from "../pages/ItemNotFound";
import { LoadingPage } from "../pages/LoadingPage";
import { Cases } from "../pages/case/Cases";
import { SignInRedirect } from "./SignIn";
import SLAReport from "../pages/SLAReport";
import CSReport from "../pages/CSReport";
import ScheduleTable from "../pages/ScheduleTable";
import TimelogsReport from "../pages/TimelogsReport";
import CustomerHealthDashboard from '../pages/customer-health-analysis/CustomerHealthDashboard';
import CustomerHealthDetail from '../pages/customer-health-analysis/CustomerHealthDetail';
import UsageMetricsDashboard from '../pages/usage-metrics/UsageMetricsDashboard';

export const AppRoutes: FunctionComponent = (): ReactElement => {
   const { error } = useAuthContext();
   return (
      <>
         <ReactNotifications />
         <AsgErrorBoundary error={error}>
            <Router>
               <Routes>
                  <Route path="/" element={<Navigate to="/login" />} />
                  <Route
                     path="/login"
                     element={<LandingPage />}
                  />
                  <Route
                     path="/support"
                     element={
                        <AuthenticatedComponent
                           fallback={<FallbackComponent />}
                        >
                           <AuthorizedApp>
                              <BaseLayout />
                           </AuthorizedApp>
                        </AuthenticatedComponent>
                     }
                  >
                     <Route index element={<Navigate to="/support/cases" />} />
                     <Route path="cases" element={<Cases />} />
                     <Route path="cases/:caseId" element={<CaseView />} />
                     <Route path="all-accounts" element={<Accounts />} />
                     <Route path="my-accounts" element={<Accounts />} />
                     <Route path="accounts/:accountId" element={<AccountDetails />} />
                     <Route path="accounts/:accountId/projects/:projectId" element={<ProjectDetails />} />
                     <Route path="projects" element={<Projects />} />
                     <Route path="projects/:projectId" element={<ProjectDetails />} />
                     <Route path="projects/:projectId/sla-report/:sysId" element={<SLAReport />} />
                     <Route path="projects/:projectId/cs-report/:sysId" element={<CSReport />} />
                     <Route path="projects/:projectId/timelogs-report" element={<TimelogsReport />} />
                     <Route path="team-schedule/:sysId?" element={<ScheduleTable />} />
                     <Route path="user-scan" element={<UserScan />} />
                     <Route path="customer-health" element={<CustomerHealthDashboard />} />
                     <Route path="customer-health/account/:accountId" element={<CustomerHealthDetail />} />
                     <Route path="usage-metrics" element={<UsageMetricsDashboard />} />
                  </Route>
                  <Route path="/signin" element={<SignInRedirect />} />
                  <Route path="/logout" element={<LoggedOutPage />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  <Route path="/not-found" element={<ItemNotFoundPage />} />
                  <Route path="*" element={<NotFoundPage />} />
               </Routes>
            </Router>
         </AsgErrorBoundary>
      </>
   );
};

const FallbackComponent = () => {
  const { signIn } = useAuthContext();
  useEffect(() => {
    signIn();
  }, [signIn]); 
  return <LoadingPage/>;
};
